// React & Hooks
import { memo, useCallback, useEffect, useState } from "react";

// API & Types
import { getTireAPI } from "services/api/calculations/tire/api";
import { TireDetailFormValues } from "services/api/calculations/tire/types";
import { Errors } from "services/forms/validation/types";
import { initialTireDetailForm } from "services/api/calculations/tire/consts";

// UI Components
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Project Specific Components
import TireEditCard from "../../../TireEditCard/TireEditCard";

// Utilities
import { useTranslate } from "helpers";

// Constants
import { CALCULATION_TIRE_CREATE_PAGE_TITLE } from "ui/components/PageTitle/consts";

// Types
import { TireDetailProps } from "./types";


const TireDetailCard: React.FC<TireDetailProps> = (props) => {
  const { tireId } = props;

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<TireDetailFormValues>(
    initialTireDetailForm
  );

  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (tireId) {
      fetchTire(tireId);
    }
  }, [tireId]);

  const fetchTire = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialTireDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getTireAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleEditSuccess = useCallback(() => {
    if (tireId) fetchTire(tireId);
    setShowEditModal(false);
  }, []);

  if (!tireId) return <></>;

  return (
    <>
      <div className="flex gap-4 ">
        <div className="rounded-sm bg-white border max-w-lg w-full">
          <div className="divide-y divide-slate-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase">
                {t("admin.calculation.setting.tire.detail.title")}
              </p>
              <div className="flex items-center gap-2 px-3 my-3 ">
                <Button
                  size="small"
                  variant="primary"
                  children={t("admin.change")}
                  onClick={() => setShowEditModal(true)}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
            <div className="flex divide-x divide-slate-200 min-h-[56px]">
              <div className="px-4 py-2 flex-1">
                <div className="text-xs font-medium uppercase text-slate-600">
                  {t("admin.calculation.setting.tire.detail.width")}
                </div>
                <div className="text-sm text-slate-500 truncate">
                  {formValues.width}
                </div>
              </div>
              <div className="px-4 py-2 flex-1">
                <div className="text-xs font-medium uppercase text-slate-600">
                  {t("admin.calculation.setting.tire.detail.height")}
                </div>
                <div className="text-sm text-slate-500 truncate">
                  {formValues.height}
                </div>
              </div>
              <div className="px-4 py-2 flex-1">
                <div className="text-xs font-medium uppercase text-slate-600">
                  {t("admin.calculation.setting.tire.detail.radius")}
                </div>
                <div className="text-sm text-slate-500 truncate">
                  {formValues.radius}
                </div>
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.setting.tire.detail.changeprice")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.changePrice} €
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.setting.tire.detail.hotelprice")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.hotelPrice} €
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-sm bg-white border max-w-lg w-full">
          <div className="divide-y divide-slate-200">
            {formValues.manufacturers?.map((item, index) => {
              return (
                <div
                  className="px-4 py-2 min-h-[56px] flex justify-between items-center text-sm font-medium "
                  key={index}
                >
                  <div className="">{item.tireManufacturer?.name}</div>
                  <div className="">{item.price} €</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <ModalHeader
            title={CALCULATION_TIRE_CREATE_PAGE_TITLE.text}
            onHide={() => setShowEditModal(false)}
          />
          <ModalBody>
            <TireEditCard tireId={tireId} onSuccess={handleEditSuccess} />
          </ModalBody>
        </Modal>
      )}
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(TireDetailCard);
